import { createContext, ReactElement, ReactNode, useContext } from 'react';
import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  SubmitHandler,
  useForm,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
  UseFormUnregister,
  UseFormWatch,
} from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import { schema, Schema } from '../components/Orcamento/data/schema';

import { useLoadingStore } from '../stores/useLoadingStore';
import { useBoostedPlanDataStore } from '../stores/useBoostedPlanDataStore';
import { set } from 'date-fns';

type PaymentFormSchema = Schema;

interface OrcamentoFormContextProps {
  register: UseFormRegister<PaymentFormSchema>;
  control: Control<PaymentFormSchema, any>;
  watch: UseFormWatch<PaymentFormSchema>;
  handleSubmit: UseFormHandleSubmit<PaymentFormSchema>;
  onSubmit: (schema: PaymentFormSchema) => void;
  errors: FieldErrors<PaymentFormSchema>;
  clearErrors: UseFormClearErrors<PaymentFormSchema>;
  unregister: UseFormUnregister<PaymentFormSchema>;
  setValue: UseFormSetValue<PaymentFormSchema>;
}

const OrcamentoFormContext = createContext({} as OrcamentoFormContextProps);

interface OrcamentoFormProviderProps {
  children: ReactNode;
}

export function OrcamentoFormProvider({
  children,
}: OrcamentoFormProviderProps): ReactElement {
  const {
    register,
    handleSubmit,
    control,
    watch,
    unregister,
    clearErrors,
    setValue,
    reset,
    formState: { errors },
  } = useForm<PaymentFormSchema>({
    resolver: zodResolver(schema),
  });

  const { setLoading } = useLoadingStore();
  const setVendedor = useBoostedPlanDataStore(x => x.setBoostPlanData);

  const onSubmit: SubmitHandler<PaymentFormSchema> = async data => {
    const newData = {
      id_vendedor: data.vendedor.id_vendedor,
      cnpj: data.empresa.cnpj.replace(/\D/g, ''),
      razao_social: data.empresa.razaoSocial,
      cpf_resp: data.empresa.Responsavel.cpf.replace(/\D/g, ''),
      nome_resp: data.empresa.Responsavel.nome,
      email_resp: data.empresa.Responsavel.email,
      telefone_resp: data.empresa.Responsavel.telefone.replace(/\D/g, ''),
      cep: data.empresa.address.cep.replace(/\D/g, ''),
      logradouro: data.empresa.address.street,
      numero: data.empresa.address.number,
      complemento: data.empresa.address.complement,
      bairro: data.empresa.address.neighborhood,
      cidade: data.empresa.address.city,
      uf: data.empresa.address.uf,
      email_vendedor: data.vendedor.email,
      produtos: data.planos.map(plano => ({
        id_produto: plano.id,
        qtd_titular: plano.qtdTitular || '0',
        qtd_dependente: plano.qtdDependente || '0',
        valor: plano.total,
      })),
    };
    setLoading(true);
    const planValue = await fetch(
      `${process.env.REACT_APP_OG_API_URL}/orcamento/generate`,
      {
        headers: {
          'content-type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(newData),
      },
    )
      .then(async response => {
        if (response.ok) {
          const responseData = await response.json();
          alert(
            `Orçamento gerado com sucesso! \nId: ${responseData.id_orcamento}`,
          );
          reset();
          setVendedor({});
          setLoading(false);
        } else {
          const errorData = await response.json();
          alert(errorData.message || 'Erro ao gerar orçamento');
        }
      })
      .catch(error => {
        alert(error.message || 'Erro ao gerar orçamento');
      });

    setLoading(false);
  };

  return (
    <OrcamentoFormContext.Provider
      value={{
        register,
        control,
        handleSubmit,
        watch,
        onSubmit,
        errors,
        clearErrors,
        unregister,
        setValue,
      }}
    >
      {children}
    </OrcamentoFormContext.Provider>
  );
}

export function useOrcamentoForm(): OrcamentoFormContextProps {
  return useContext(OrcamentoFormContext);
}
